import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";
import { Formik } from "formik";
import Logo from "../../assets/AuthLogo.svg";
import bgImage from "../../assets/loginpage-bg.png";
import { useDispatch, useSelector } from "react-redux";
import { userLogin } from "../../store/actions/authActions";
import { useNavigate } from "react-router-dom";
import { User, Lock, LogIn } from "lucide-react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Apis from "../../utils/apis";
import axios from "axios";
import { toast } from "react-toastify";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auth } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState("login");
  const [greeting, setGreeting] = useState("");

  const idleTimeoutRef = useRef(null);

  /** Mouse position for gradient effect */
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);

  /** Idle state for gradient animation */
  const [isIdle, setIsIdle] = useState(true);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Get time-based greeting
  useEffect(() => {
    const getGreeting = () => {
      const hour = new Date().getHours();
      if (hour < 12) return "Good Morning";
      if (hour < 17) return "Good Afternoon";
      return "Good Evening";
    };
    setGreeting(getGreeting());
  }, []);

  // Track cursor position - optimized with idle detection
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMouseX(e.clientX);
      setMouseY(e.clientY);
      setIsIdle(false);

      // Clear existing timeout
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      // Set new timeout to mark as idle after 2 seconds
      idleTimeoutRef.current = setTimeout(() => {
        setIsIdle(true);
      }, 2000);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (auth) {
      navigate(auth.role === "projector_viewer" ? "/projector" : "/dashboard");
    }
  }, [auth, navigate]);

  useEffect(() => {
    setShowPassword(false);
  }, [mode]);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f9fbfd] via-white to-[#e3f2fd] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Image with Slide Up Animation */}
      <div className="absolute inset-0 pointer-events-none animate-slide-up-bg">
        <img
          src={bgImage}
          alt="Background"
          className="w-full h-full object-cover opacity-30"
        />
      </div>

      {/* Animated Background Orbs - Cursor Reactive */}
      <div
        className="absolute w-96 h-96 rounded-full bg-gradient-to-br from-[#2e67be]/20 to-[#4985f2]/20 blur-3xl pointer-events-none"
        style={{
          left: "10%",
          top: "20%",
          transform: `translate(${mouseX * 0.008}px, ${mouseY * 0.008}px)`,
          willChange: "transform",
        }}
      />
      <div
        className="absolute w-80 h-80 rounded-full bg-gradient-to-br from-[#4985f2]/15 to-[#739fe1]/15 blur-3xl pointer-events-none"
        style={{
          right: "15%",
          bottom: "15%",
          transform: `translate(${-mouseX * 0.006}px, ${-mouseY * 0.006}px)`,
          willChange: "transform",
        }}
      />

      {/* Floating Geometric Elements - Cursor Reactive */}
      {/* Square */}
      <div
        className="absolute w-16 h-16 bg-gradient-to-br from-[#4985f2]/20 to-[#2e67be]/20 backdrop-blur-sm border border-[#4985f2]/30 pointer-events-none"
        style={{
          left: "15%",
          top: "15%",
          transform: `translate(${mouseX * 0.04}px, ${mouseY * 0.04}px) rotate(${mouseX * 0.05}deg)`,
          willChange: "transform",
        }}
      />

      {/* Triangle */}
      <div
        className="absolute w-0 h-0 border-l-[30px] border-l-transparent border-r-[30px] border-r-transparent border-b-[50px] border-b-[#739fe1]/25 pointer-events-none"
        style={{
          right: "20%",
          top: "25%",
          transform: `translate(${-mouseX * 0.03}px, ${mouseY * 0.03}px) rotate(${-mouseX * 0.03}deg)`,
          willChange: "transform",
        }}
      />

      {/* Diamond */}
      <div
        className="absolute w-12 h-12 bg-gradient-to-br from-[#2e67be]/15 to-[#4985f2]/15 backdrop-blur-sm border border-[#2e67be]/30 pointer-events-none"
        style={{
          left: "75%",
          top: "60%",
          transform: `translate(${mouseX * 0.035}px, ${-mouseY * 0.035}px) rotate(45deg)`,
          willChange: "transform",
        }}
      />

      {/* Hexagon shape using clip-path */}
      <div
        className="absolute w-14 h-14 bg-gradient-to-br from-[#4985f2]/20 to-[#739fe1]/20 backdrop-blur-sm pointer-events-none"
        style={{
          clipPath:
            "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
          left: "12%",
          bottom: "20%",
          transform: `translate(${mouseX * 0.045}px, ${-mouseY * 0.045}px) rotate(${mouseX * 0.04}deg)`,
          willChange: "transform",
        }}
      />

      {/* Circle */}
      <div
        className="absolute w-10 h-10 rounded-full bg-gradient-to-br from-[#739fe1]/30 to-[#4985f2]/30 backdrop-blur-sm border border-[#739fe1]/40 pointer-events-none"
        style={{
          right: "12%",
          bottom: "30%",
          transform: `translate(${-mouseX * 0.05}px, ${mouseY * 0.05}px)`,
          willChange: "transform",
        }}
      />

      {/* Rounded Square */}
      <div
        className="absolute w-14 h-14 rounded-xl bg-gradient-to-br from-[#2e67be]/18 to-[#4985f2]/18 backdrop-blur-sm border border-[#2e67be]/25 pointer-events-none"
        style={{
          left: "80%",
          top: "18%",
          transform: `translate(${-mouseX * 0.038}px, ${mouseY * 0.038}px) rotate(${mouseX * 0.02}deg)`,
          willChange: "transform",
        }}
      />

      {/* Small Diamond */}
      <div
        className="absolute w-8 h-8 bg-gradient-to-br from-[#4985f2]/25 to-[#739fe1]/25 backdrop-blur-sm border border-[#4985f2]/35 pointer-events-none"
        style={{
          left: "25%",
          bottom: "35%",
          transform: `translate(${mouseX * 0.042}px, ${-mouseY * 0.042}px) rotate(45deg)`,
          willChange: "transform",
        }}
      />

      {/* Pentagon using clip-path */}
      <div
        className="absolute w-12 h-12 bg-gradient-to-br from-[#739fe1]/22 to-[#2e67be]/22 backdrop-blur-sm pointer-events-none"
        style={{
          clipPath: "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
          right: "25%",
          bottom: "22%",
          transform: `translate(${-mouseX * 0.048}px, ${-mouseY * 0.048}px) rotate(${-mouseX * 0.03}deg)`,
          willChange: "transform",
        }}
      />

      {/* Login Card */}
      <div
        className="relative z-10 w-full max-w-md bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 ease-out"
        style={{
          transform: isIdle
            ? "perspective(1000px) rotateX(0deg) rotateY(0deg)"
            : `perspective(1000px) rotateX(${(mouseY - window.innerHeight / 2) * -0.01}deg) rotateY(${(mouseX - window.innerWidth / 2) * 0.01}deg)`,
          transition: isIdle ? "transform 0.8s ease-out" : "none",
        }}
      >
        {/* Logo & Greeting */}
        <div className="p-10 text-center border-b border-gray-100">
          <div className="flex items-center justify-center mb-6">
            <img src={Logo} alt="ZELLIX Logo" className="h-12" />
          </div>
          <p className="text-[#739fe1]">
            {mode === "login" && `${greeting}! Welcome back`}
            {mode === "forgot" && "Reset your password"}
          </p>
        </div>

        {/* Login Form */}
        {mode === "login" && (
          <Formik
            initialValues={{ email: "", password: "" }}
            // initialValues={{ email: "ca.nyo.a.ya.n@gmail.com", password: "" }}
            // initialValues={{ email: "rushibhumkar11@gmail.com", password: "" }}
            validate={(values) => {
              const errors = {};
              if (!values.email) {
                errors.email = "Email is Required";
              } else if (
                process.env.NODE_ENV === "production" &&
                !values.password
              ) {
                errors.password = "Password is Required";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
              ) {
                errors.email = "Invalid email address";
              }
              return errors;
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setLoading(true);
              const { type, payload } = await dispatch(userLogin(values));
              setLoading(false);
              if (type === "user/login/fulfilled") {
                const url = sessionStorage.getItem("requestUrl");
                navigate(payload?.role === "projector_viewer" ? "/projector" : (url ? url : "/dashboard"));
                sessionStorage.removeItem("requestUrl");
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              handleBlur,
            }) => (
              <form
                id="login-form"
                method="post"
                autoComplete="on"
                onSubmit={handleSubmit}
                className="p-10 space-y-6 overflow-visible"
              >
                {/* Email Field */}
                <div className="space-y-2 relative overflow-visible">
                  <label htmlFor="login-email" className="block text-sm text-gray-700">
                    Email Address
                  </label>
                  <div className="relative overflow-visible">
                    <User
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#739fe1]"
                      strokeWidth={2}
                    />
                    <input
                      id="login-email"
                      type="email"
                      name="email"
                      inputMode="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      autoComplete="username"
                      style={{ textTransform: "none" }}
                      value={values.email}
                      onChange={handleChange}
                      onInput={handleChange}
                      onBlur={handleBlur}
                      placeholder="email"
                      className="w-full h-12 pl-12 pr-4 bg-[#f9fbfd] border border-[#739fe1]/20 rounded-xl text-sm text-[#2e67be] placeholder:text-[#739fe1]/50 focus:outline-none focus:ring-2 focus:ring-[#4985f2]/30 focus:border-[#4985f2] transition-all"
                    />
                  </div>
                  <Typography
                    sx={{ color: "red", textAlign: "left", fontSize: "11px" }}
                  >
                    {errors.email}
                  </Typography>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label htmlFor="login-password" className="block text-sm text-gray-700">
                    Password
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#739fe1]"
                      strokeWidth={2}
                    />
                    <input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      autoComplete="current-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      value={values.password}
                      onChange={handleChange}
                      onInput={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your password"
                      className="w-full h-12 pl-12 pr-12 bg-[#f9fbfd] border border-[#739fe1]/20 rounded-xl text-sm text-[#2e67be] placeholder:text-[#739fe1]/50 focus:outline-none focus:ring-2 focus:ring-[#4985f2]/30 focus:border-[#4985f2] transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                      aria-controls="login-password"
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#739fe1] hover:text-[#2e67be] rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4985f2]"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </button>
                  </div>
                  <Typography
                    sx={{ color: "red", textAlign: "left", fontSize: "11px" }}
                  >
                    {errors.password}
                  </Typography>
                </div>

                {/* Forgot Password Link */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setMode("forgot")}
                    className="text-sm text-[#4985f2] hover:text-[#2e67be] transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-[#2e67be]/40 transition-all active:scale-98 group disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span>Loading...</span>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <LogIn
                        className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                        strokeWidth={2}
                      />
                    </>
                  )}
                </button>
              </form>
            )}
          </Formik>
        )}

        {/* Forgot Password Form */}
        {mode === "forgot" && (
          <Formik
            initialValues={{
              email: "",
            }}
            validate={(values) => {
              const errors = {};
              if (!values.email) {
                errors.email = "Email is Required";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
              ) {
                errors.email = "Invalid email address";
              }
              return errors;
            }}
            onSubmit={async (values) => {
              setLoading(true);
              try {
                const { data } = await axios.post(
                  `${Apis.users}/forgetPassword`,
                  values,
                );
                toast.success(data);
              } catch (err) {
                toast.error(err.response.data);
              }
              setLoading(false);
            }}
          >
            {({ values, errors, handleChange, handleSubmit }) => (
              <form onSubmit={handleSubmit} className="p-10 space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label className="block text-sm text-gray-700">
                    Email Address
                  </label>
                  <div className="relative">
                    <User
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#739fe1]"
                      strokeWidth={2}
                    />
                    <input
                      type="email"
                      name="email"
                      inputMode="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      autoComplete="email"
                      style={{ textTransform: "none" }}
                      value={values.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className="w-full h-12 pl-12 pr-4 bg-[#f9fbfd] border border-[#739fe1]/20 rounded-xl text-sm text-[#2e67be] placeholder:text-[#739fe1]/50 focus:outline-none focus:ring-2 focus:ring-[#4985f2]/30 focus:border-[#4985f2] transition-all"
                    />
                  </div>
                  <Typography
                    sx={{ color: "red", textAlign: "left", fontSize: "11px" }}
                  >
                    {errors.email}
                  </Typography>
                  <p className="text-xs text-[#739fe1] ml-1">
                    We'll send you a password reset link
                  </p>
                </div>

                {/* Reset Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-[#2e67be]/40 transition-all active:scale-98 group disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span>Loading...</span>
                  ) : (
                    <>
                      <span>Send Reset Link</span>
                      <LogIn
                        className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                        strokeWidth={2}
                      />
                    </>
                  )}
                </button>

                {/* Back to Login Link */}
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Remember your password?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      className="text-[#4985f2] hover:text-[#2e67be] transition-colors"
                    >
                      Sign In
                    </button>
                  </p>
                </div>
              </form>
            )}
          </Formik>
        )}

        {/* Footer */}
        <div className="px-10 pb-10 text-center">
          <p className="text-xs text-[#739fe1]">
            © 2025 ZELLIX. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
